import type {LocationQueryValue} from 'vue-router';

// eslint-disable-next-line no-control-regex
const INVISIBLE_CHARS = /[\u0000-\u001F]/g;

export function sanitizeStringArrayParam(param: LocationQueryValue | LocationQueryValue[]) {
    return [param]
        .flat()
        .filter((value): value is string => !!value)
        .map((value) => value.replace(INVISIBLE_CHARS, ' ').trim())
        .filter((value) => value.length > 0);
}

export function sanitizeEnumArrayParam<T extends string>(param: LocationQueryValue | LocationQueryValue[], acceptedEnumValues: object): T[] {
    return sanitizeStringArrayParam(param).filter((value): value is T => Object.values(acceptedEnumValues).includes(value));
}

export function sanitizeSingleNumberParam(param: LocationQueryValue | LocationQueryValue[]): number | undefined {
    return (
        sanitizeStringArrayParam(param)
            .map((value) => Number.parseFloat(value))
            .find((value) => !Number.isNaN(value)) || undefined
    );
}

export function sanitizeSingleIntegerParam(param: LocationQueryValue | LocationQueryValue[]): number | undefined {
    return (
        sanitizeStringArrayParam(param)
            .map((value) => Number.parseInt(value))
            .find((value) => !Number.isNaN(value)) || undefined
    );
}

export function sanitizeSingleStringParam(param: LocationQueryValue | LocationQueryValue[]): string | undefined {
    return sanitizeStringArrayParam(param)[0] || undefined;
}

export function sanitizeSingleEnumParam<T extends string>(
    param: LocationQueryValue | LocationQueryValue[],
    acceptedEnumValues: object,
): T | undefined {
    return (sanitizeEnumArrayParam(param, acceptedEnumValues)[0] as T) || undefined;
}

export function sanitizeBooleanParam(param: LocationQueryValue | LocationQueryValue[]): boolean | undefined {
    const lowercaseValues = sanitizeStringArrayParam(param).map((s) => s.toLowerCase());
    if (lowercaseValues.includes('false')) {
        return false;
    }
    if (lowercaseValues.includes('true')) {
        return true;
    }
    return undefined;
}
